import { BaseRowData } from '@adv-frontend/data-provider';
import { castRef, genericMemo } from '@direct-frontend/stdlib';
import { forwardRef, useCallback, useMemo } from 'react';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { cn } from '../../../lib/css/className/className';
import { IconArrowDown, IconArrowUp } from '../../../icons';
import { OmitSelectProps, Select } from '../../Select';
import { TriggerProps } from '../../Select/Trigger/types';
import { getSelectTriggerContent, isMultiSelectProps } from '../../Select/utils';
import { useFilterChipAddonSizes } from '../BaseFilterChip/BaseFilterChip';
import { FilterChip, FilterChipProps } from '../FilterChip';
import { FilterItem, FilterItemProps } from '../FilterItem';

getPlatformStyles({
    common: import('./FilterSelect.css'),
});

const cls = cn('FilterSelect');

export type FilterSelectProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
> = OmitSelectProps<ValueType, SectionType, RowData, 'size' | 'color'> &
    Pick<FilterItemProps, 'size' | 'color' | 'onRemove'> & {
        /** Включен ли каунтер, по умолчанию true */
        isCounterEnabled?: boolean;

        /** Параметры для триггера фильтра */
        filterChipProps?: Pick<FilterChipProps, 'error'>;
    };

/**
 * Компонент для отображения фильтра с выбором. Может быть одиночный выбор или мультивыбор.
 * В случае одиночного – текст ключа фильтра заменяется на выбранное значение,
 * в случае множественного – появляется каунтер
 */
export const FilterSelect = genericMemo(function FilterSelect<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
>(props: FilterSelectProps<ValueType, SectionType, RowData>) {
    const {
        color,
        size,
        className,
        'data-testid': dataTestId,
        onRemove,
        isCounterEnabled = true,
        filterChipProps,
        ...restProps
    } = props;

    const isFullWidth = props.width === 'max';
    const isMultiSelect = isMultiSelectProps(restProps);

    const getRenderTrigger = useCallback(
        <ValueType extends string>() =>
            forwardRef<HTMLElement, TriggerProps<ValueType>>(function RenderTrigger(triggerProps, forwardedRef) {
                const { isOpened, value, placeholder } = triggerProps;
                const { iconSize } = useFilterChipAddonSizes(size);
                const badgeCounter = props?.value?.length ?? 0;

                return (
                    <FilterItem
                        color={color}
                        size={size}
                        width={isFullWidth ? 'max' : undefined}
                        keyChip={
                            <FilterChip
                                {...filterChipProps}
                                {...triggerProps}
                                ref={castRef(forwardedRef, 'div')}
                                data-testid={dataTestId}
                                counterValue={isCounterEnabled && isMultiSelect ? badgeCounter : undefined}
                                addonRight={
                                    isOpened ? (
                                        <IconArrowUp color={'gray'} size={iconSize} />
                                    ) : (
                                        <IconArrowDown color={'gray'} size={iconSize} />
                                    )
                                }
                            >
                                {isMultiSelect ? placeholder : (getSelectTriggerContent(value) ?? placeholder)}
                            </FilterChip>
                        }
                        onRemove={onRemove}
                    />
                );
            }),
        [
            size,
            props?.value?.length,
            color,
            filterChipProps,
            dataTestId,
            isCounterEnabled,
            isMultiSelect,
            onRemove,
            isFullWidth,
        ],
    );

    const renderTrigger = useMemo(() => getRenderTrigger<ValueType>(), [getRenderTrigger]);
    const renderDataProviderTrigger = useMemo(() => getRenderTrigger<string>(), [getRenderTrigger]);

    const selectProps =
        'dataProvider' in props
            ? {
                  ...props,
                  renderTrigger: renderDataProviderTrigger,
              }
            : {
                  ...props,
                  renderTrigger,
              };

    return (
        <Select<ValueType, SectionType, RowData>
            {...selectProps}
            size={size}
            color={color}
            className={cls('', { width: isFullWidth ? 'max' : undefined }, [className])}
            data-testid={dataTestId}
        />
    );
});
